import { useState } from "react";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Loader2 } from "lucide-react";

type Props = {
  domain: string;
  email: string;
  auditId: string;
  apiUrl: string;
  onSuccess: () => void;
};

export function PaymentForm({ domain, email, auditId, apiUrl, onSuccess }: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);

    try {
      const { error: stripeErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: { receipt_email: email },
      });

      if (stripeErr) {
        setError(stripeErr.message ?? "Payment could not be completed.");
        setLoading(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        try {
          await fetch(`${apiUrl}/api/confirm-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ audit_id: auditId, stripe_payment_id: paymentIntent.id }),
          });
        } catch {
          /* non-fatal */
        }
        onSuccess();
        return;
      }

      setError("Payment status unclear. Please contact support.");
    } catch {
      setError("We couldn't reach our servers. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePay} className="space-y-6">
      <div>
        <h3 className="heading-h3 text-white">Complete your payment</h3>
        <p className="mt-1 text-[14px] text-[color:hsl(var(--muted-foreground))]">
          Secure payment via Stripe.
        </p>
      </div>

      <div className="rounded-xl border border-[color:hsl(var(--border))] bg-[color:hsl(var(--elevated))] p-4">
        <div className="flex items-center justify-between text-[14px]">
          <span className="text-[color:hsl(var(--muted-foreground))]">
            Full website audit — {domain}
          </span>
          <span className="tabular text-white">$197.00</span>
        </div>
        <hr className="my-3 border-[color:hsl(var(--border))]" />
        <div className="flex items-center justify-between text-[15px] font-semibold text-white">
          <span>Total</span>
          <span className="tabular">$197.00</span>
        </div>
      </div>

      <PaymentElement />

      {error && (
        <p className="text-[13px] text-[color:hsl(var(--destructive))]" role="alert">
          {error}
        </p>
      )}

      <button
        type="submit"
        className="cta-primary cta-primary--block"
        disabled={!stripe || loading}
        aria-busy={loading}
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Processing…
          </>
        ) : (
          "Pay $197"
        )}
      </button>
    </form>
  );
}
